import React, { useEffect, useState, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { memo } from "react";
import Navbar from "../src/components/ChatWithData/Navbar";
import {
  setOtpStatusFlag,
  setChatWithDataResponse,
  setSelectedChatData,
} from "./redux/actions/ChatWithData/ChatWithData";

// Pages where navbar should NOT be rendered at the top level (it's rendered inside the component)
const PAGES_WITH_INTERNAL_NAVBAR = ["/chat-with-data", "/share/id"];

const ChatWithDataMain = React.lazy(() =>
  import("./components/ChatWithData/ChatWithDataMain")
);
const LandingPage = React.lazy(() =>
  import("./components/ChatWithData/LandingPage")
);
const PricingPage = React.lazy(() =>
  import("./components/ChatWithData/PricingPage")
);
const SignUp = React.lazy(() => import("./components/ChatWithData/SignUp"));
const SignIn = React.lazy(() => import("./components/ChatWithData/SignIn"));
const UserSignIn = React.lazy(() =>
  import("./components/ChatWithData/UserSignIn")
);
const ForgotPasswordPage = React.lazy(() =>
  import("./components/ChatWithData/ForgotPasswordPage")
);
const CreateNewPasswordPage = React.lazy(() =>
  import("./components/ChatWithData/CreateNewPasswordPage")
);
const Payment = React.lazy(() => import("./components/ChatWithData/Payment"));
const ProfilePage = React.lazy(() =>
  import("./components/ChatWithData/ProfilePage")
);
const DevDebugPage = React.lazy(() =>
  import("./components/ChatWithData/DevDebugPage")
);

// Loader component to reduce repetition
const PageLoader = () => (
  <div className="suspense-loader-container">
    <div className="lds-ring">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  </div>
);

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const share_id = window.location.pathname.startsWith("/share/id")
    ? window.location.pathname.split("/")[3]
    : null;

  let p_token = localStorage.getItem("p_token");
  const [isShowChatHistorySidebar, setIsShowChatHistorySidebar] = useState(false);
  const [isShowSidebar, setIsShowSidebar] = useState(false);
  const [text, setText] = useState("");

  useEffect(() => {
    if (location.pathname === "/") {
      dispatch(setOtpStatusFlag(false));

      // Reset Chat Response
      let obj = {
        persist_data: [],
        data: [],
        total_length: null,
      };
      dispatch(setChatWithDataResponse(obj));

      // Reset selected chat data
      dispatch(setSelectedChatData(null));
    } else if (location.pathname === "/sign-up") {
      document.body.style.overflow = "hidden";
    } else if (location.pathname === "/chat-with-data") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Cleanup function to reset overflow when component unmounts or path changes
    return () => {
      document.body.style.overflow = "";
    };
  }, [location.pathname]);

  // Redirect to landing page, if not logged in and try to change path
  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/");
    } else if (!p_token && location.pathname === "/sign-in") {
      navigate("/sign-in");
    } else if (!p_token && location.pathname === "/sign-up") {
      // Do Nothing, otherwise data reset
    } else if (!p_token && location.pathname === "/locobuzz-sign-in") {
      navigate("/locobuzz-sign-in");
    } else if (
      !p_token &&
      (location.pathname.startsWith("/locobuzz-sign-in/query") ||
        location.pathname.startsWith("/locobuzz-sign-in/share"))
    ) {
      // Do nothing
    } else if (location.pathname === "/forgot-password") {
      navigate("/forgot-password");
    } else if (location.pathname === "/create-password") {
      // Do Nothing, otherwise data reset
    } else if (p_token && location.pathname === "/pricing") {
      // Do Nothing, user is already on pricing page
    } else if (p_token && location.pathname === "/payment") {
      // Do Nothing, otherwise data reset
    } else if (p_token && location.pathname === "/profile") {
      navigate("/profile");
    } else if (
      location.pathname === "/chat-with-data" ||
      location.pathname.startsWith("/share/id/")
    ) {
      // Allow guest users to access chat-with-data
      // No redirect needed - both authenticated and guest users can access
    } else if (p_token) {
      navigate("/chat-with-data");
    } else if (
      !p_token &&
      (location.pathname.startsWith("/sign-up/query") ||
        location.pathname.startsWith("/sign-up/share"))
    ) {
      // Do nothing
    } else if (
      !p_token &&
      (location.pathname.startsWith("/sign-in/query") ||
        location.pathname.startsWith("/sign-in/share"))
    ) {
      // Do nothing
    } else if (!p_token) {
      // Redirect unauthenticated users to landing page for all other routes
      navigate("/");
    }
  }, [location.pathname]);

  // Check if current page should have navbar rendered internally (not at top level)
  const shouldHideTopNavbar = PAGES_WITH_INTERNAL_NAVBAR.some(path => 
    location.pathname === path || location.pathname.startsWith(path + "/")
  );

  return (
    <>
      <div className="main-div">
        {/* Only render Navbar at top level for pages that don't have internal navbar */}
        {!shouldHideTopNavbar && (
          <Navbar
            isShowSidebar={isShowSidebar}
            setIsShowSidebar={setIsShowSidebar}
            isShowChatHistorySidebar={isShowChatHistorySidebar}
            setIsShowChatHistorySidebar={setIsShowChatHistorySidebar}
            text={text}
            setText={setText}
            share_id={share_id}
          />
        )}
        <Routes>
          <Route path="/" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <LandingPage />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/sign-up" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <SignUp />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/sign-up/query/:value" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <SignUp />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/sign-up/share/:share_id" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <SignUp />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/sign-in" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <UserSignIn />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/sign-in/query/:value" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <UserSignIn />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/sign-in/share/:share_id" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <UserSignIn />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/locobuzz-sign-in" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <SignIn />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/locobuzz-sign-in/query/:value" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <SignIn />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/locobuzz-sign-in/share/:share_id" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <SignIn />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/forgot-password" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <ForgotPasswordPage />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/create-password" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <CreateNewPasswordPage />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/pricing" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <PricingPage />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/payment" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <Payment />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/profile" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <ProfilePage
                  isShowSidebar={isShowSidebar}
                  setIsShowSidebar={setIsShowSidebar}
                  isShowChatHistorySidebar={isShowChatHistorySidebar}
                  setIsShowChatHistorySidebar={setIsShowChatHistorySidebar}
                  text={text}
                  setText={setText}
                />
              </ErrorBoundary>
            </Suspense>
          } />
          {process.env.NODE_ENV === "development" && (
            <Route path="/dev-debug" element={
              <Suspense fallback={<PageLoader />}>
                <ErrorBoundary>
                  <DevDebugPage />
                </ErrorBoundary>
              </Suspense>
            } />
          )}
          <Route path="/chat-with-data" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <ChatWithDataMain
                  isShowSidebar={isShowSidebar}
                  setIsShowSidebar={setIsShowSidebar}
                  isShowChatHistorySidebar={isShowChatHistorySidebar}
                  setIsShowChatHistorySidebar={setIsShowChatHistorySidebar}
                  text={text}
                  setText={setText}
                />
              </ErrorBoundary>
            </Suspense>
          } />
          <Route path="/share/id/:share_id" element={
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary>
                <ChatWithDataMain
                  isShowSidebar={isShowSidebar}
                  setIsShowSidebar={setIsShowSidebar}
                  isShowChatHistorySidebar={isShowChatHistorySidebar}
                  setIsShowChatHistorySidebar={setIsShowChatHistorySidebar}
                  text={text}
                  setText={setText}
                />
              </ErrorBoundary>
            </Suspense>
          } />
        </Routes>
      </div>
    </>
  );
};

export default memo(AppRoutes);
